import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { api, API, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Upload, RefreshCw, Search, FileText, Link2, Link2Off, EyeOff, XCircle, Download,
  ClipboardList, ArrowUpRight, ArrowDownRight, ListChecks, AlertTriangle, Landmark, History,
} from "lucide-react";
import { toast } from "sonner";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB — espelha limite do backend (D2)
const ALLOWED_EXT = ["ofx", "qfx", "csv", "txt"];

const STATUS_EXTRATO = [
  { key: "importado", label: "Importado" },
  { key: "cancelado", label: "Cancelado" },
];

const STATUS_LINHA = [
  { key: "pendente", label: "Pendente" },
  { key: "conciliada", label: "Conciliada" },
  { key: "ignorada", label: "Ignorada" },
];

const AUDIT_LABEL = {
  extrato_importado: "Extrato importado",
  extrato_cancelado: "Extrato cancelado",
  linha_conciliada: "Linha conciliada",
  linha_desconciliada: "Linha desconciliada",
  linha_ignorada: "Linha ignorada",
};

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function StatusLinhaBadge({ status }) {
  const map = {
    pendente: { label: "Pendente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    conciliada: { label: "Conciliada", cls: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20" },
    ignorada: { label: "Ignorada", cls: "bg-stone-100 text-stone-500 border-stone-300" },
  };
  const it = map[status] || map.pendente;
  return (
    <span data-testid={`cb-status-linha-${status}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${it.cls}`}>
      {it.label}
    </span>
  );
}

function StatusExtratoBadge({ status }) {
  const cancel = status === "cancelado";
  return (
    <span data-testid={`cb-status-extrato-${status}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${
        cancel ? "bg-stone-100 text-stone-500 border-stone-300"
               : "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20"
      }`}>
      {cancel ? "Cancelado" : "Importado"}
    </span>
  );
}

function TipoTag({ tipo }) {
  const isIn = tipo === "entrada";
  const Icon = isIn ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${isIn ? "text-[#083A6B]" : "text-[#D4A652]"}`}>
      <Icon size={14} strokeWidth={2} />
      {isIn ? "Entrada" : "Saída"}
    </span>
  );
}

function extractDetail(err, fallback) {
  const st = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (st === 401) return "Sessão expirada. Faça login novamente.";
  if (st === 403) return typeof detail === "string" ? detail : "Sem permissão para esta ação.";
  if (st === 409) return typeof detail === "string" ? detail : "Conflito: operação não permitida no estado atual.";
  if (st === 413) return typeof detail === "string" ? detail : "Arquivo excede o limite permitido (10 MB).";
  if (st === 422) return typeof detail === "string" ? detail : "Dados inválidos.";
  if (st >= 500) return "Erro inesperado no servidor. Tente novamente.";
  return typeof detail === "string" ? detail : (fallback || "Erro na operação.");
}

export default function ConciliacaoBancaria() {
  const [me, setMe] = useState(null);
  const [contas, setContas] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  // Filtros extratos
  const [filtros, setFiltros] = useState({
    conta_financeira_id: "all", status: "all", de: "", ate: "",
  });
  const [busca, setBusca] = useState("");

  const [extratos, setExtratos] = useState([]);
  const [loadingExtratos, setLoadingExtratos] = useState(true);
  const [erroExtratos, setErroExtratos] = useState(null);

  // Import
  const [impCF, setImpCF] = useState("");
  const [impFile, setImpFile] = useState(null);
  const [impLoading, setImpLoading] = useState(false);
  const [impResult, setImpResult] = useState(null);
  const [impError, setImpError] = useState(null);
  const fileInputRef = useRef(null);

  // Selected extrato view
  const [selExtrato, setSelExtrato] = useState(null); // header full
  const [linhas, setLinhas] = useState([]);
  const [linhasLoading, setLinhasLoading] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [filtroLinha, setFiltroLinha] = useState("all");

  // Candidatos dialog
  const [candLinha, setCandLinha] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candSelected, setCandSelected] = useState(null);
  const [candMotivo, setCandMotivo] = useState("");
  const [conciliando, setConciliando] = useState(false);

  // Desconciliar
  const [descAlvo, setDescAlvo] = useState(null);
  const [descMotivo, setDescMotivo] = useState("");
  const [desconciliando, setDesconciliando] = useState(false);

  // Ignorar
  const [ignAlvo, setIgnAlvo] = useState(null);
  const [ignMotivo, setIgnMotivo] = useState("");
  const [ignorando, setIgnorando] = useState(false);

  // Cancelar extrato
  const [cancelAlvo, setCancelAlvo] = useState(null);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [cancelando, setCancelando] = useState(false);

  // Auditoria dialog
  const [audAlvo, setAudAlvo] = useState(null);
  const [audEventos, setAudEventos] = useState([]);
  const [audLoading, setAudLoading] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));
  const isContador = !!(me && me.is_accountant && !me.is_admin && !me.is_finance);

  // Load referências
  const loadRefs = useCallback(async () => {
    setLoadingRefs(true);
    try {
      const [u, cf] = await Promise.all([
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/contas-financeiras").catch(() => ({ data: [] })),
      ]);
      setMe(u.data);
      setContas(Array.isArray(cf.data) ? cf.data : []);
    } finally {
      setLoadingRefs(false);
    }
  }, []);

  const loadExtratos = useCallback(async () => {
    setLoadingExtratos(true); setErroExtratos(null);
    try {
      const params = {};
      if (filtros.conta_financeira_id !== "all") params.conta_financeira_id = filtros.conta_financeira_id;
      if (filtros.status !== "all") params.status = filtros.status;
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;
      const { data } = await api.get("/extratos-bancarios", { params });
      setExtratos(Array.isArray(data) ? data : []);
    } catch (err) {
      setExtratos([]);
      setErroExtratos(extractDetail(err, "Não foi possível carregar os extratos."));
    } finally {
      setLoadingExtratos(false);
    }
  }, [filtros]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { loadExtratos(); }, [loadExtratos]);

  const contasAtivas = useMemo(() => contas.filter((c) => c.ativo !== false), [contas]);
  const contaMap = useMemo(() => Object.fromEntries(contas.map((c) => [c.id, c])), [contas]);

  const extratosFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return extratos;
    return extratos.filter((e) => {
      const alvo = [
        e.arquivo_nome, e.formato, e.status,
        contaMap[e.conta_financeira_id]?.nome, e.arquivo_hash_prefix,
      ].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [extratos, busca, contaMap]);

  // Import handlers
  const onSelectFile = (e) => {
    setImpError(null); setImpResult(null);
    const f = e.target.files?.[0];
    if (!f) { setImpFile(null); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setImpError(`Extensão inválida. Use: ${ALLOWED_EXT.join(", ")}.`);
      setImpFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setImpError(`Arquivo excede o limite de 10 MB (${fmtBytes(f.size)}).`);
      setImpFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setImpFile(f);
  };

  const doImport = async (e) => {
    e.preventDefault();
    if (!impCF) { setImpError("Selecione a Conta Financeira."); return; }
    if (!impFile) { setImpError("Selecione um arquivo OFX ou CSV."); return; }
    setImpLoading(true); setImpError(null); setImpResult(null);
    try {
      const form = new FormData();
      form.append("conta_financeira_id", impCF);
      form.append("file", impFile);
      const { data } = await axios.post(`${API}/extratos-bancarios/importar`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImpResult(data);
      toast.success("Extrato importado");
      setImpFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadExtratos();
    } catch (err) {
      const msg = extractDetail(err, "Falha ao importar extrato.");
      setImpError(msg);
      toast.error(msg);
    } finally {
      setImpLoading(false);
    }
  };

  // Abrir detalhes do extrato: linhas + resumo
  const abrirDetalhe = async (eb) => {
    setSelExtrato(eb); setDetalheOpen(true);
    setLinhas([]); setResumo(null); setFiltroLinha("all");
    await Promise.all([carregarLinhas(eb.id, "all"), carregarResumo(eb.id)]);
  };

  const carregarLinhas = async (extratoId, statusFiltro) => {
    setLinhasLoading(true);
    try {
      const params = {};
      if (statusFiltro && statusFiltro !== "all") params.status = statusFiltro;
      const { data } = await api.get(`/extratos-bancarios/${extratoId}/linhas`, { params });
      setLinhas(Array.isArray(data) ? data : []);
    } catch (err) {
      setLinhas([]);
      toast.error(extractDetail(err, "Erro ao carregar linhas."));
    } finally {
      setLinhasLoading(false);
    }
  };

  const carregarResumo = async (extratoId) => {
    try {
      const { data } = await api.get(`/extratos-bancarios/${extratoId}/resumo`);
      setResumo(data);
    } catch { setResumo(null); }
  };

  // Trocar filtro de linhas
  useEffect(() => {
    if (selExtrato && detalheOpen) carregarLinhas(selExtrato.id, filtroLinha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroLinha]);

  // Candidatos
  const abrirCandidatos = async (linha) => {
    setCandLinha(linha); setCandidatos([]); setCandSelected(null); setCandMotivo("");
    setCandLoading(true);
    try {
      const { data } = await api.get(`/extratos-bancarios/linhas/${linha.id}/candidatos`);
      const arr = Array.isArray(data) ? data : [];
      setCandidatos(arr);
      if (arr.length === 1) setCandSelected(arr[0].id);
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao buscar candidatos."));
    } finally {
      setCandLoading(false);
    }
  };

  const confirmarConciliacao = async () => {
    if (!candLinha || !candSelected) return;
    setConciliando(true);
    try {
      await api.post(`/extratos-bancarios/linhas/${candLinha.id}/conciliar`, {
        movimento_financeiro_id: candSelected,
        motivo: candMotivo.trim() || null,
      });
      toast.success("Linha conciliada");
      setCandLinha(null); setCandidatos([]); setCandSelected(null); setCandMotivo("");
      if (selExtrato) {
        await Promise.all([carregarLinhas(selExtrato.id, filtroLinha), carregarResumo(selExtrato.id)]);
      }
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao conciliar."));
    } finally {
      setConciliando(false);
    }
  };

  // Desconciliar
  const executarDesconciliar = async () => {
    if (!descAlvo) return;
    setDesconciliando(true);
    try {
      await api.post(`/extratos-bancarios/linhas/${descAlvo.id}/desconciliar`, {
        motivo: descMotivo.trim() || null,
      });
      toast.success("Linha desconciliada");
      setDescAlvo(null); setDescMotivo("");
      if (selExtrato) {
        await Promise.all([carregarLinhas(selExtrato.id, filtroLinha), carregarResumo(selExtrato.id)]);
      }
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao desconciliar."));
    } finally {
      setDesconciliando(false);
    }
  };

  // Ignorar
  const executarIgnorar = async () => {
    if (!ignAlvo) return;
    setIgnorando(true);
    try {
      await api.post(`/extratos-bancarios/linhas/${ignAlvo.id}/ignorar`, {
        motivo: ignMotivo.trim() || null,
      });
      toast.success("Linha ignorada");
      setIgnAlvo(null); setIgnMotivo("");
      if (selExtrato) {
        await Promise.all([carregarLinhas(selExtrato.id, filtroLinha), carregarResumo(selExtrato.id)]);
      }
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao ignorar."));
    } finally {
      setIgnorando(false);
    }
  };

  // Cancelar extrato
  const executarCancelar = async () => {
    if (!cancelAlvo) return;
    if (!cancelMotivo.trim()) { toast.error("Informe o motivo do cancelamento"); return; }
    setCancelando(true);
    try {
      await api.post(`/extratos-bancarios/${cancelAlvo.id}/cancelar`, {
        motivo: cancelMotivo.trim(),
      });
      toast.success("Extrato cancelado");
      setCancelAlvo(null); setCancelMotivo("");
      // Se o detalhe está aberto neste extrato, refresh
      if (selExtrato && selExtrato.id === cancelAlvo.id) {
        setSelExtrato({ ...selExtrato, status: "cancelado" });
      }
      loadExtratos();
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao cancelar extrato."));
    } finally {
      setCancelando(false);
    }
  };

  // Download
  const doDownload = async (eb) => {
    try {
      const resp = await axios.get(`${API}/extratos-bancarios/${eb.id}/arquivo`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = eb.arquivo_nome || `extrato-${eb.id}.bin`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao baixar arquivo."));
    }
  };

  // Auditoria
  const abrirAuditoria = async (eb) => {
    setAudAlvo(eb); setAudEventos([]); setAudLoading(true);
    try {
      const { data } = await api.get(`/extratos-bancarios/${eb.id}/auditoria`);
      setAudEventos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(extractDetail(err, "Erro ao carregar auditoria."));
    } finally {
      setAudLoading(false);
    }
  };

  // ----- RENDER -----
  return (
    <div data-testid="page-conciliacao-bancaria">
      <PageHeader
        eyebrow="Financeiro · Conciliação Bancária"
        title="Conciliação Bancária"
        description="Importe extratos OFX/CSV, revise as transações e concilie com os movimentos internos. Auditoria completa por extrato."
      />

      {loadingRefs ? (
        <div data-testid="cb-loading-refs" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando…
        </div>
      ) : (
        <>
          {/* SEÇÃO 1 — IMPORTAR */}
          <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 mb-8" data-testid="cb-secao-importar">
            <div className="flex items-center gap-2 mb-4">
              <Upload size={16} className="text-[#083A6B]" />
              <h2 className="font-serif text-xl text-stone-900">Importar extrato</h2>
            </div>
            {!podeEscrever ? (
              <div className="text-sm text-stone-500" data-testid="cb-import-readonly">
                Perfil somente leitura: importação disponível apenas para Admin/Financeiro.
              </div>
            ) : (
              <form onSubmit={doImport} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="cb-imp-cf">Conta Financeira *</Label>
                  <Select value={impCF} onValueChange={setImpCF}>
                    <SelectTrigger id="cb-imp-cf" data-testid="select-cb-imp-cf">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {contasAtivas.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cb-imp-file">Arquivo (OFX ou CSV) *</Label>
                  <Input id="cb-imp-file" ref={fileInputRef} type="file"
                    accept=".ofx,.qfx,.csv,.txt"
                    data-testid="input-cb-imp-file" onChange={onSelectFile} />
                  <div className="text-[11px] text-stone-500 mt-1">
                    Máximo: 10 MB · Formatos: OFX, CSV.
                  </div>
                </div>
                <div className="flex items-end">
                  <Button type="submit" data-testid="btn-cb-importar"
                    disabled={impLoading || !impCF || !impFile}>
                    {impLoading ? "Enviando…" : (<><Upload size={14} /> Importar Extrato</>)}
                  </Button>
                </div>
                {impError && (
                  <div className="md:col-span-3 text-sm text-[#D4A652] bg-[#F5E4DD] border border-[#D4A652]/25 rounded-md p-3"
                    data-testid="cb-imp-error">
                    {impError}
                  </div>
                )}
                {impResult && (
                  <div className="md:col-span-3 bg-[#EAF0EA] border border-[#083A6B]/20 rounded-md p-4"
                    data-testid="cb-imp-result">
                    <div className="text-[11px] uppercase tracking-widest text-[#083A6B] mb-2">Resultado da importação</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-stone-800">
                      <div><b>Arquivo:</b> {impResult.extrato?.arquivo_nome}</div>
                      <div><b>Formato:</b> {impResult.extrato?.formato?.toUpperCase()}</div>
                      <div><b>Período:</b> {impResult.extrato?.periodo_de ? `${fmtDate(impResult.extrato.periodo_de)} — ${fmtDate(impResult.extrato.periodo_ate)}` : "—"}</div>
                      <div><b>Linhas:</b> {impResult.extrato?.qtd_linhas ?? impResult.resumo?.criadas ?? 0}</div>
                      <div><b>Importadas:</b> {impResult.resumo?.criadas ?? 0}</div>
                      <div><b>Duplicidades:</b> {(impResult.resumo?.duplicadas_intra_arquivo ?? 0) + (impResult.resumo?.duplicadas_ja_existentes ?? 0)}</div>
                      <div><b>Inválidas:</b> {impResult.resumo?.invalidas ?? 0}</div>
                      <div><b>Pendentes:</b> {impResult.resumo?.criadas ?? 0}</div>
                    </div>
                  </div>
                )}
              </form>
            )}
          </section>

          {/* SEÇÃO 2 — EXTRATOS */}
          <section data-testid="cb-secao-extratos">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={16} className="text-[#083A6B]" />
              <h2 className="font-serif text-xl text-stone-900">Extratos importados</h2>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-500">Conta</Label>
                <Select value={filtros.conta_financeira_id}
                  onValueChange={(v) => setFiltros({ ...filtros, conta_financeira_id: v })}>
                  <SelectTrigger data-testid="filter-cb-cf" className="w-[200px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {contas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
                <Select value={filtros.status} onValueChange={(v) => setFiltros({ ...filtros, status: v })}>
                  <SelectTrigger data-testid="filter-cb-status" className="w-[150px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {STATUS_EXTRATO.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-500">De</Label>
                <Input type="date" data-testid="filter-cb-de" value={filtros.de}
                  onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} className="w-[150px]" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
                <Input type="date" data-testid="filter-cb-ate" value={filtros.ate}
                  onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} className="w-[150px]" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input value={busca} data-testid="filter-cb-busca"
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Arquivo, formato, hash..." className="pl-8" />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={loadExtratos} data-testid="btn-cb-refresh">
                <RefreshCw size={14} />
              </Button>
            </div>

            {/* Lista */}
            {loadingExtratos ? (
              <div data-testid="cb-extratos-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
                Carregando extratos…
              </div>
            ) : erroExtratos ? (
              <div data-testid="cb-extratos-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
                <AlertTriangle size={22} className="mx-auto mb-2" /> {erroExtratos}
              </div>
            ) : extratosFiltrados.length === 0 ? (
              <div data-testid="cb-extratos-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
                <div className="font-serif text-xl text-stone-900 mb-2">Nenhum extrato encontrado.</div>
                <p className="text-sm text-stone-600">
                  Importe um arquivo OFX ou CSV na seção acima para começar.
                </p>
              </div>
            ) : (
              <>
                {/* Mobile: cards */}
                <div className="md:hidden space-y-3">
                  {extratosFiltrados.map((e) => (
                    <div key={e.id} data-testid={`cb-extrato-card-${e.id}`}
                      className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-1">
                            <Landmark size={12} /> {contaMap[e.conta_financeira_id]?.nome || "—"}
                          </div>
                          <div className="font-medium text-stone-900 truncate">{e.arquivo_nome}</div>
                        </div>
                        <StatusExtratoBadge status={e.status} />
                      </div>
                      <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                        <div><b>Formato:</b> {e.formato?.toUpperCase()}</div>
                        <div><b>Período:</b> {e.periodo_de ? `${fmtDate(e.periodo_de)} — ${fmtDate(e.periodo_ate)}` : "—"}</div>
                        <div><b>Linhas:</b> {e.qtd_linhas ?? 0}</div>
                        <div><b>Importado:</b> {e.created_at?.slice(0, 10) || "—"}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => abrirDetalhe(e)}
                          data-testid={`btn-cb-linhas-${e.id}`}><ListChecks size={14} /> Linhas</Button>
                        <Button size="sm" variant="outline" onClick={() => abrirAuditoria(e)}
                          data-testid={`btn-cb-auditoria-${e.id}`}><History size={14} /> Auditoria</Button>
                        <Button size="sm" variant="outline" onClick={() => doDownload(e)}
                          data-testid={`btn-cb-download-${e.id}`}><Download size={14} /> Baixar</Button>
                        {podeEscrever && e.status !== "cancelado" && (
                          <Button size="sm" variant="outline" onClick={() => setCancelAlvo(e)}
                            data-testid={`btn-cb-cancelar-${e.id}`}><XCircle size={14} /> Cancelar</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: tabela */}
                <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-stone-50 hover:bg-stone-50">
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Conta</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Arquivo</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Formato</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Período</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Linhas</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Importado</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {extratosFiltrados.map((e) => (
                        <TableRow key={e.id} data-testid={`cb-extrato-row-${e.id}`}>
                          <TableCell className="text-stone-700 text-sm">{contaMap[e.conta_financeira_id]?.nome || "—"}</TableCell>
                          <TableCell className="text-stone-700 text-sm max-w-xs truncate">
                            <div className="flex items-center gap-2">
                              <FileText size={14} className="text-stone-400" /> {e.arquivo_nome}
                            </div>
                          </TableCell>
                          <TableCell className="text-stone-700 text-sm uppercase">{e.formato}</TableCell>
                          <TableCell className="text-stone-700 text-sm whitespace-nowrap">
                            {e.periodo_de ? `${fmtDate(e.periodo_de)} — ${fmtDate(e.periodo_ate)}` : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-stone-700">{e.qtd_linhas ?? 0}</TableCell>
                          <TableCell><StatusExtratoBadge status={e.status} /></TableCell>
                          <TableCell className="text-stone-700 text-sm whitespace-nowrap">{e.created_at?.slice(0, 10) || "—"}</TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(e)}
                                aria-label="Ver linhas" data-testid={`btn-cb-linhas-${e.id}`}>
                                <ListChecks size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => abrirAuditoria(e)}
                                aria-label="Auditoria" data-testid={`btn-cb-auditoria-${e.id}`}>
                                <History size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => doDownload(e)}
                                aria-label="Baixar arquivo" data-testid={`btn-cb-download-${e.id}`}>
                                <Download size={14} />
                              </Button>
                              {podeEscrever && e.status !== "cancelado" && (
                                <Button size="sm" variant="ghost" onClick={() => setCancelAlvo(e)}
                                  aria-label="Cancelar extrato" data-testid={`btn-cb-cancelar-${e.id}`}>
                                  <XCircle size={14} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {/* DIALOG DETALHE (LINHAS + RESUMO) */}
      <Dialog open={detalheOpen} onOpenChange={(v) => {
        setDetalheOpen(v);
        if (!v) { setSelExtrato(null); setLinhas([]); setResumo(null); }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cb-detalhe">
          <DialogHeader>
            <DialogTitle>
              {selExtrato ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span>Extrato · {selExtrato.arquivo_nome}</span>
                  <StatusExtratoBadge status={selExtrato.status} />
                </div>
              ) : "Extrato"}
            </DialogTitle>
            <DialogDescription>
              {selExtrato && (
                <>
                  {contaMap[selExtrato.conta_financeira_id]?.nome || "—"} ·{" "}
                  {selExtrato.periodo_de ? `${fmtDate(selExtrato.periodo_de)} — ${fmtDate(selExtrato.periodo_ate)}` : "sem período"}{" "}
                  · {selExtrato.formato?.toUpperCase()}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Resumo */}
          {resumo && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cb-resumo">
              <div className="bg-stone-50 border border-stone-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total</div>
                <div className="font-serif text-xl tabular-nums">{resumo.total}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-amber-700">Pendentes</div>
                <div className="font-serif text-xl tabular-nums text-amber-800" data-testid="cb-resumo-pendentes">{resumo.pendentes}</div>
              </div>
              <div className="bg-[#EAF0EA] border border-[#083A6B]/20 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#083A6B]">Conciliadas</div>
                <div className="font-serif text-xl tabular-nums text-[#083A6B]" data-testid="cb-resumo-conciliadas">{resumo.conciliadas}</div>
              </div>
              <div className="bg-stone-100 border border-stone-300 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Ignoradas</div>
                <div className="font-serif text-xl tabular-nums text-stone-600">{resumo.ignoradas}</div>
              </div>
              {(resumo.saldo_contabil_extrato !== null && resumo.saldo_contabil_extrato !== undefined) && (
                <div className="bg-stone-50 border border-stone-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Saldo contábil (extrato)</div>
                  <div className="font-serif text-lg tabular-nums">{brl(resumo.saldo_contabil_extrato)}</div>
                </div>
              )}
              {(resumo.saldo_derivado_movimentos !== null && resumo.saldo_derivado_movimentos !== undefined) && (
                <div className="bg-stone-50 border border-stone-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Saldo derivado (MF)</div>
                  <div className="font-serif text-lg tabular-nums">{brl(resumo.saldo_derivado_movimentos)}</div>
                </div>
              )}
              {(resumo.divergencia !== null && resumo.divergencia !== undefined) && (
                <div className="col-span-2 md:col-span-2 bg-orange-50 border border-orange-200 rounded-md p-3" data-testid="cb-resumo-divergencia">
                  <div className="text-[10px] uppercase tracking-widest text-orange-700">Divergência (informativa)</div>
                  <div className="font-serif text-lg tabular-nums text-orange-800">{brl(resumo.divergencia)}</div>
                </div>
              )}
            </div>
          )}

          {/* Filtro linhas */}
          <div className="flex items-end gap-3 mb-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-500">Filtrar linhas</Label>
              <Select value={filtroLinha} onValueChange={setFiltroLinha}>
                <SelectTrigger data-testid="filter-cb-linha-status" className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {STATUS_LINHA.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-stone-500 pb-2">
              {linhas.length} linha(s) exibida(s)
            </div>
          </div>

          {/* Linhas */}
          {linhasLoading ? (
            <div data-testid="cb-linhas-loading" className="p-6 text-center text-stone-500">Carregando linhas…</div>
          ) : linhas.length === 0 ? (
            <div data-testid="cb-linhas-empty" className="p-6 text-center text-stone-500">Sem linhas nesse filtro.</div>
          ) : (
            <div className="border border-stone-200 rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50 hover:bg-stone-50">
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Data</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Descrição</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">FITID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={l.id} data-testid={`cb-linha-row-${l.id}`}>
                      <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(l.data_transacao)}</TableCell>
                      <TableCell><TipoTag tipo={l.tipo} /></TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${l.tipo === "saida" ? "text-[#D4A652]" : "text-[#083A6B]"}`}>
                        {l.tipo === "saida" ? "− " : "+ "}{brl(l.valor)}
                      </TableCell>
                      <TableCell className="text-stone-700 text-sm max-w-xs truncate">{l.descricao_original || "—"}</TableCell>
                      <TableCell className="text-stone-500 text-xs font-mono">{l.fitid || "—"}</TableCell>
                      <TableCell><StatusLinhaBadge status={l.status_conciliacao} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          {podeEscrever && l.status_conciliacao === "pendente" && selExtrato?.status !== "cancelado" && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => abrirCandidatos(l)}
                                aria-label="Ver candidatos" data-testid={`btn-cb-candidatos-${l.id}`}>
                                <Link2 size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setIgnAlvo(l); setIgnMotivo(""); }}
                                aria-label="Ignorar" data-testid={`btn-cb-ignorar-${l.id}`}>
                                <EyeOff size={14} />
                              </Button>
                            </>
                          )}
                          {podeEscrever && l.status_conciliacao === "conciliada" && (
                            <Button size="sm" variant="ghost" onClick={() => { setDescAlvo(l); setDescMotivo(""); }}
                              aria-label="Desconciliar" data-testid={`btn-cb-desconciliar-${l.id}`}>
                              <Link2Off size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG CANDIDATOS */}
      <Dialog open={!!candLinha} onOpenChange={(v) => {
        if (!v) { setCandLinha(null); setCandidatos([]); setCandSelected(null); setCandMotivo(""); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cb-candidatos">
          <DialogHeader>
            <DialogTitle>Candidatos para conciliação</DialogTitle>
            <DialogDescription>
              {candLinha && (
                <>Linha: {fmtDate(candLinha.data_transacao)} · <TipoTag tipo={candLinha.tipo} /> · <b className="tabular-nums">{brl(candLinha.valor)}</b> · {candLinha.descricao_original || "—"}</>
              )}
            </DialogDescription>
          </DialogHeader>

          {candLoading ? (
            <div className="p-6 text-center text-stone-500" data-testid="cb-cand-loading">Buscando candidatos…</div>
          ) : candidatos.length === 0 ? (
            <div className="p-6 text-center text-stone-500 border border-dashed border-stone-300 rounded-md"
              data-testid="cb-cand-empty">
              Nenhum movimento compatível encontrado.
            </div>
          ) : (
            <>
              <div className="border border-stone-200 rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500">Data</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500">Descrição</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500">Conta</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {candidatos.map((m) => (
                      <TableRow key={m.id} data-testid={`cb-cand-row-${m.id}`}
                        className={`cursor-pointer ${candSelected === m.id ? "bg-[#EAF0EA]" : ""}`}
                        onClick={() => setCandSelected(m.id)}>
                        <TableCell>
                          <input type="radio" name="candidato" checked={candSelected === m.id}
                            onChange={() => setCandSelected(m.id)}
                            data-testid={`cb-cand-radio-${m.id}`} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{fmtDate(m.data_movimento)}</TableCell>
                        <TableCell><TipoTag tipo={m.tipo} /></TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${m.tipo === "saida" ? "text-[#D4A652]" : "text-[#083A6B]"}`}>
                          {brl(m.valor)}
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">{m.descricao || "—"}</TableCell>
                        <TableCell className="text-sm">{contaMap[m.conta_financeira_id]?.nome || "—"}</TableCell>
                        <TableCell className="text-xs">{m.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {podeEscrever && (
                <div className="mt-3">
                  <Label htmlFor="cb-cand-motivo">Motivo (opcional)</Label>
                  <Input id="cb-cand-motivo" data-testid="input-cb-cand-motivo"
                    value={candMotivo} onChange={(e) => setCandMotivo(e.target.value)}
                    placeholder="Observação da conciliação" />
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCandLinha(null)} disabled={conciliando}>Fechar</Button>
            {podeEscrever && (
              <Button data-testid="btn-cb-confirmar-conciliar"
                onClick={() => {
                  if (!candSelected) { toast.error("Selecione um candidato"); return; }
                  if (window.confirm("Confirmar conciliação desta transação com o movimento selecionado?")) {
                    confirmarConciliacao();
                  }
                }}
                disabled={conciliando || !candSelected || candidatos.length === 0}>
                {conciliando ? "Conciliando…" : "Conciliar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG DESCONCILIAR */}
      <Dialog open={!!descAlvo} onOpenChange={(v) => { if (!v) { setDescAlvo(null); setDescMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cb-desconciliar">
          <DialogHeader>
            <DialogTitle>Desconciliar linha?</DialogTitle>
            <DialogDescription>
              A linha voltará ao status "Pendente" e o vínculo com o movimento financeiro será desfeito.
            </DialogDescription>
          </DialogHeader>
          {descAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div>{fmtDate(descAlvo.data_transacao)} · <TipoTag tipo={descAlvo.tipo} /> · <b className="tabular-nums">{brl(descAlvo.valor)}</b></div>
              <div className="text-xs text-stone-500 truncate">{descAlvo.descricao_original || "—"}</div>
            </div>
          )}
          <div>
            <Label htmlFor="cb-desc-motivo">Motivo (opcional)</Label>
            <Input id="cb-desc-motivo" data-testid="input-cb-desc-motivo"
              value={descMotivo} onChange={(e) => setDescMotivo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDescAlvo(null)} disabled={desconciliando}>Cancelar</Button>
            <Button data-testid="btn-cb-confirmar-desconciliar"
              onClick={executarDesconciliar} disabled={desconciliando}>
              {desconciliando ? "Desconciliando…" : "Desconciliar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG IGNORAR */}
      <Dialog open={!!ignAlvo} onOpenChange={(v) => { if (!v) { setIgnAlvo(null); setIgnMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cb-ignorar">
          <DialogHeader>
            <DialogTitle>Ignorar linha?</DialogTitle>
            <DialogDescription>
              A linha será marcada como "Ignorada" e não aparecerá em pendências. Você pode informar um motivo opcional.
            </DialogDescription>
          </DialogHeader>
          {ignAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div>{fmtDate(ignAlvo.data_transacao)} · <TipoTag tipo={ignAlvo.tipo} /> · <b className="tabular-nums">{brl(ignAlvo.valor)}</b></div>
              <div className="text-xs text-stone-500 truncate">{ignAlvo.descricao_original || "—"}</div>
            </div>
          )}
          <div>
            <Label htmlFor="cb-ign-motivo">Motivo (opcional)</Label>
            <Input id="cb-ign-motivo" data-testid="input-cb-ign-motivo"
              value={ignMotivo} onChange={(e) => setIgnMotivo(e.target.value)}
              placeholder="Ex.: tarifa não contabilizada" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIgnAlvo(null)} disabled={ignorando}>Cancelar</Button>
            <Button data-testid="btn-cb-confirmar-ignorar"
              onClick={executarIgnorar} disabled={ignorando}>
              {ignorando ? "Ignorando…" : "Ignorar linha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG CANCELAR EXTRATO */}
      <Dialog open={!!cancelAlvo} onOpenChange={(v) => { if (!v) { setCancelAlvo(null); setCancelMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cb-cancelar">
          <DialogHeader>
            <DialogTitle>Cancelar extrato?</DialogTitle>
            <DialogDescription>
              O extrato será marcado como cancelado. Não haverá exclusão física; conciliações já realizadas serão mantidas na auditoria.
            </DialogDescription>
          </DialogHeader>
          {cancelAlvo && (
            <div className="text-sm text-stone-700">
              <b>{cancelAlvo.arquivo_nome}</b>
              <div className="text-xs text-stone-500">
                {contaMap[cancelAlvo.conta_financeira_id]?.nome || "—"} · {cancelAlvo.formato?.toUpperCase()}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="cb-cancel-motivo">Motivo *</Label>
            <Input id="cb-cancel-motivo" data-testid="input-cb-cancel-motivo"
              value={cancelMotivo} onChange={(e) => setCancelMotivo(e.target.value)}
              placeholder="Descreva o motivo do cancelamento" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelAlvo(null)} disabled={cancelando}>Voltar</Button>
            <Button data-testid="btn-cb-confirmar-cancelar"
              onClick={executarCancelar} disabled={cancelando || !cancelMotivo.trim()}>
              {cancelando ? "Cancelando…" : "Cancelar extrato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG AUDITORIA */}
      <Dialog open={!!audAlvo} onOpenChange={(v) => { if (!v) { setAudAlvo(null); setAudEventos([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-cb-auditoria">
          <DialogHeader>
            <DialogTitle>Auditoria do extrato</DialogTitle>
            <DialogDescription>
              {audAlvo?.arquivo_nome} · {contaMap[audAlvo?.conta_financeira_id]?.nome || "—"}
            </DialogDescription>
          </DialogHeader>
          {audLoading ? (
            <div className="p-6 text-center text-stone-500">Carregando auditoria…</div>
          ) : audEventos.length === 0 ? (
            <div className="p-6 text-center text-stone-500">Nenhum evento registrado.</div>
          ) : (
            <ol className="relative border-l border-stone-200 ml-3 space-y-4" data-testid="cb-audit-timeline">
              {audEventos.map((ev) => (
                <li key={ev.id} className="ml-4">
                  <div className="absolute -left-1.5 w-3 h-3 bg-[#083A6B] rounded-full mt-1.5" />
                  <div className="text-sm font-medium text-stone-800">
                    {AUDIT_LABEL[ev.acao] || ev.acao}
                  </div>
                  <div className="text-xs text-stone-500">
                    {ev.quando?.slice(0, 19).replace("T", " ")} · {ev.por || "—"}
                  </div>
                  {ev.extras && Object.keys(ev.extras).length > 0 && (
                    <div className="mt-1 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-md p-2 font-mono">
                      {Object.entries(ev.extras).map(([k, v]) => (
                        <div key={k}><b>{k}:</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
